import React, { useEffect, useState } from "react";
import { Box, Divider, useBreakpointValue } from "@chakra-ui/react";
import { useCurrency } from "../../context/CurrencyContext";
import FreezeDialog from "./FreezeDialog";
import ConfiscateDialog from "./ConfiscateDialog";
import {
    WalletEmptyState,
    WalletHeader,
    FrozenBanner,
    WalletBalance,
    WalletHistory,
    WalletFooterNote
} from "./WalletDisplays";
import WalletActions from "./WalletActions";

/**
 * Resolves a currency code to its symbol using the currency context.
 * @param {string} currencyCode - The currency code to look up
 * @param {Function} getCurrencySymbolByCode - Resolver function from currency context
 * @param {Function} setCurrency - State setter for the resolved symbol
 * @returns {Promise<void>}
 */
async function fetchCurrencySymbol(currencyCode, getCurrencySymbolByCode, setCurrency) {
    if (currencyCode) {
        const symbol = await getCurrencySymbolByCode(currencyCode);
        setCurrency(symbol);
    }
}

/**
 * Displays wallet information for an artist: balance, history, frozen status,
 * and admin actions (freeze/unfreeze, confiscate).
 * @param {Object} props
 * @param {Array} props.wallets - Array of wallet objects (first one is used)
 * @param {string} props.currencyCode - Currency code to resolve symbol
 * @param {string} props.userId - User ID for freeze/confiscate API calls
 * @param {number} props.adminRight - Connected admin's right level (1=admin, 2=super admin)
 * @returns {React.ReactElement}
 */
function ArtistWallet({ wallets, currencyCode, userId, adminRight }) {
    const [currency, setCurrency] = useState('');
    const [wallet, setWallet] = useState(wallets?.[0]);
    const [freezeDialogOpen, setFreezeDialogOpen] = useState(false);
    const [confiscateDialogOpen, setConfiscateDialogOpen] = useState(false);
    const isMobile = useBreakpointValue({ base: true, sm: false });
    const { getCurrencySymbolByCode } = useCurrency();

    useEffect(() => { setWallet(wallets?.[0]); }, [wallets]);

    useEffect(() => {
        fetchCurrencySymbol(currencyCode, getCurrencySymbolByCode, setCurrency);
    }, [currencyCode, getCurrencySymbolByCode]);

    if (!wallet || Object.keys(wallet).length === 0) {
        return <WalletEmptyState isMobile={isMobile} />;
    }

    return (
        <Box>
            <WalletMainContent wallet={wallet} currency={currency} isMobile={isMobile} />
            <WalletActions
                wallet={wallet}
                adminRight={adminRight}
                onFreezeClick={() => setFreezeDialogOpen(true)}
                onConfiscateClick={() => setConfiscateDialogOpen(true)}
                isMobile={isMobile}
            />
            <WalletDialogs
                wallet={wallet}
                userId={userId}
                setWallet={setWallet}
                freezeDialogOpen={freezeDialogOpen}
                setFreezeDialogOpen={setFreezeDialogOpen}
                confiscateDialogOpen={confiscateDialogOpen}
                setConfiscateDialogOpen={setConfiscateDialogOpen}
            />
        </Box>
    );
}

/**
 * Renders the main wallet content: header, frozen banner, balance, history, and footer.
 * @param {Object} props
 * @param {Object} props.wallet - Wallet data object
 * @param {string} props.currency - Resolved currency symbol
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function WalletMainContent({ wallet, currency, isMobile }) {
    return (
        <>
            <WalletHeader isMobile={isMobile} />
            <Divider mb={6} borderColor="rgba(255, 255, 255, 0.08)" />
            {wallet?.frozen && <FrozenBanner wallet={wallet} isMobile={isMobile} />}
            <WalletBalance wallet={wallet} currency={currency} isMobile={isMobile} />
            <WalletHistory wallet={wallet} isMobile={isMobile} />
            <WalletFooterNote isMobile={isMobile} />
        </>
    );
}

/**
 * Renders the freeze and confiscate dialogs for the wallet.
 * @param {Object} props
 * @param {Object} props.wallet - Wallet data object
 * @param {string} props.userId - Target user ID
 * @param {Function} props.setWallet - State setter for wallet data
 * @param {boolean} props.freezeDialogOpen - Whether the freeze dialog is open
 * @param {Function} props.setFreezeDialogOpen - Setter for freeze dialog state
 * @param {boolean} props.confiscateDialogOpen - Whether the confiscate dialog is open
 * @param {Function} props.setConfiscateDialogOpen - Setter for confiscate dialog state
 * @returns {React.ReactElement}
 */
function WalletDialogs({ wallet, userId, setWallet, freezeDialogOpen, setFreezeDialogOpen, confiscateDialogOpen, setConfiscateDialogOpen }) {
    return (
        <>
            <FreezeDialog
                open={freezeDialogOpen}
                onClose={() => setFreezeDialogOpen(false)}
                wallet={wallet}
                userId={userId}
                setWallet={setWallet}
            />
            <ConfiscateDialog
                open={confiscateDialogOpen}
                onClose={() => setConfiscateDialogOpen(false)}
                userId={userId}
            />
        </>
    );
}

export default ArtistWallet;
